import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'

import { Buyer, PriceRange } from 'types'
import {
  addDateUnit,
  isDateAfter,
  isDateBefore,
  subtractDateUnit,
} from 'utils/dates'
import { decimalWithCommas, numberWithSeparator } from 'utils/formatters'
import { fromGlobalId } from 'utils/transformations/graphql'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import { OrderData } from '../components'

type BuyerData = Pick<Buyer, 'name' | 'email'>

export const buyerDataToBuyerId = (data: BuyerData): string =>
  `${data.name}:${data.email}`

export const isLinesheetVisibleForRetailer = ({
  collections,
  retailerProfile,
}: {
  collections: OrderData['collections']
  retailerProfile: OrderData['retailerProfile']
}) => {
  const visibilitiesWanted = [
    'PUBLIC',
    'ALL_CONNECTIONS',
    'OWNER_PUBLIC',
    'OWNER_ALL_CONNECTIONS',
  ]

  const isVisible = collections?.edges?.some((collection) =>
    visibilitiesWanted.includes(collection?.node?.visibility ?? ''),
  )
  if (isVisible) {
    return true
  }

  return collections?.edges?.some((collection) =>
    collection?.node?.retailers?.some(
      (retailer) =>
        fromGlobalId(retailer.retailerAccount?.id).id ===
        fromGlobalId(retailerProfile?.id).id,
    ),
  )
}

export const formatPriceRange = (priceRange: PriceRange) => {
  if (priceRange?.maxPrice === priceRange?.minPrice) {
    return numberWithSeparator(decimalWithCommas(priceRange?.minPrice), ',')
  }
  return `${numberWithSeparator(
    decimalWithCommas(priceRange?.minPrice),
    ',',
  )} ${EMPTY_CELL_CONTENT} ${numberWithSeparator(
    decimalWithCommas(priceRange?.maxPrice),
    ',',
  )}`
}

export const getOrderSeparatedComments = (comments?: string | null) => {
  if (isEmpty(comments)) {
    return []
  }
  const separatedComments = compact(comments?.split('<eop>'))
  return separatedComments.map((comment) => {
    // Split on the first ": " to separate the author from the message. Matching
    // with [\s\S] rather than splitting on /: (.+)?/ because "." stops at a
    // newline, which silently dropped every line of a message after the first.
    // sc-259268
    const authoredComment = comment.match(/^([\s\S]*?): ([\s\S]*)$/)
    return authoredComment === null
      ? { message: comment }
      : {
          prefix: authoredComment[1].replace(/(\r\n|\n|\r)/, ''),
          message: authoredComment[2],
        }
  })
}

export const isValidDate = (date: Date) => {
  const oneYearAgo = subtractDateUnit(new Date(), 1, 'years')
  const twoYearsAhead = addDateUnit(new Date(), 2, 'years')
  return (
    isDateAfter(date, oneYearAgo) && isDateBefore(date, twoYearsAhead, 'day')
  )
}
